import { branding } from '@guridosdrones/shared';
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { PlayCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FadeIn } from '@/components';

const PARALLAX_SPRING = { damping: 30, stiffness: 90, mass: 0.5 };
const COORDS = { lat: '-31.7654', lon: '-52.3376' };

function useCurrentTime() {
  const [time, setTime] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

function HudText({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={`font-mono text-[10px] tracking-widest text-primary/80 uppercase sm:text-xs ${className}`}
    >
      {children}
    </span>
  );
}

function SignalBars({ strength = 4 }: { strength?: number }) {
  return (
    <span
      className="inline-flex items-end gap-0.5"
      role="img"
      aria-label={`Sinal: ${strength} de 5`}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={`block w-0.75 rounded-sm ${i <= strength ? 'bg-primary/80' : 'bg-primary/20'}`}
          style={{ height: `${4 + i * 2}px` }}
        />
      ))}
    </span>
  );
}

export function HeroCourse() {
  const sectionRef = useRef<HTMLElement>(null);
  const time = useCurrentTime();
  const [batteryLevel] = useState(() => 85 + Math.floor(Math.random() * 12));

  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const imagesRef = useRef<HTMLImageElement[]>([]);
  const frameCount = 121;

  useEffect(() => {
    // Preload all frames
    const images: HTMLImageElement[] = [];
    for (let i = 1; i <= frameCount; i++) {
      const img = new Image();
      const frameNum = i.toString().padStart(3, '0');
      img.src = `/hero-frames/${frameNum}.webp`;
      images.push(img);
    }
    imagesRef.current = images;

    // Wait for the first frame to draw it immediately
    const firstImage = images[0];
    if (firstImage) {
      firstImage.onload = () => {
        const canvas = canvasRef.current;
        if (canvas) {
          contextRef.current = canvas.getContext('2d');
          canvas.width = 1176;
          canvas.height = 780;
          contextRef.current?.drawImage(firstImage, 0, 0, canvas.width, canvas.height);
        }
      };
    }
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  const smoothProgress = useSpring(scrollYProgress, {
    damping: 50,
    stiffness: 400,
  });

  useMotionValueEvent(smoothProgress, 'change', (latest) => {
    const frameIndex = Math.min(frameCount - 1, Math.floor(latest * frameCount));
    const img = imagesRef.current[frameIndex];

    if (img?.complete && canvasRef.current && contextRef.current) {
      contextRef.current.drawImage(img, 0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  });

  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);

  const springX = useSpring(mouseX, PARALLAX_SPRING);
  const springY = useSpring(mouseY, PARALLAX_SPRING);

  const bgX = useTransform(springX, [0, 1], [12, -12]);
  const bgY = useTransform(springY, [0, 1], [8, -8]);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      mouseX.set((e.clientX - rect.left) / rect.width);
      mouseY.set((e.clientY - rect.top) / rect.height);
    },
    [mouseX, mouseY]
  );

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || window.matchMedia('(max-width: 768px)').matches) return;

    section.addEventListener('mousemove', handleMouseMove);
    return () => section.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  const timestamp = time.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <div ref={containerRef} className="relative h-[250vh]">
      <section
        ref={sectionRef}
        id="start"
        className="sticky top-0 flex min-h-svh items-end overflow-hidden bg-background"
      >
        {/* === BACKGROUND VIDEO CANVAS WITH PARALLAX === */}
        <motion.div className="absolute -inset-5 z-0" style={{ x: bgX, y: bgY }}>
          <canvas ref={canvasRef} className="h-full w-full object-cover" tabIndex={-1} />
        </motion.div>

        {/* === OVERLAYS === */}
        <div className="absolute inset-0 z-1 bg-background/40 dark:bg-black/50" />
        <div className="absolute inset-0 z-1 bg-linear-to-t from-background/90 via-background/20 to-background/30 dark:from-black/80 dark:via-black/20 dark:to-black/40" />
        <div className="absolute inset-0 z-1 bg-linear-to-r from-background/50 via-transparent to-transparent dark:from-black/40" />

        {/* Subtle vignette */}
        <div className="absolute inset-0 z-1 bg-[radial-gradient(ellipse_at_center,transparent_50%,var(--color-background)_100%)] opacity-50 dark:bg-[radial-gradient(ellipse_at_center,transparent_50%,rgba(0,0,0,0.5)_100%)] dark:opacity-100" />

        {/* Scanline overlay (very subtle) */}
        <div
          className="pointer-events-none absolute inset-0 z-2 opacity-[0.03] dark:opacity-[0.03]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(128,128,128,0.1) 2px, rgba(128,128,128,0.1) 4px)',
          }}
        />

        {/* === FPV HUD FRAME === */}
        <div className="pointer-events-none absolute inset-0 z-3" aria-hidden="true">
          {/* Corner brackets */}
          <svg
            className="absolute top-4 left-4 h-8 w-8 text-primary/30 sm:top-6 sm:left-6 sm:h-10 sm:w-10"
            viewBox="0 0 40 40"
            fill="none"
            role="img"
            aria-label="HUD corner"
          >
            <path d="M0 12V0h12" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          <svg
            className="absolute top-4 right-4 h-8 w-8 text-primary/30 sm:top-6 sm:right-6 sm:h-10 sm:w-10"
            viewBox="0 0 40 40"
            fill="none"
            role="img"
            aria-label="HUD corner"
          >
            <path d="M40 12V0H28" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          <svg
            className="absolute bottom-4 left-4 h-8 w-8 text-primary/30 sm:bottom-6 sm:left-6 sm:h-10 sm:w-10"
            viewBox="0 0 40 40"
            fill="none"
            role="img"
            aria-label="HUD corner"
          >
            <path d="M0 28v12h12" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          <svg
            className="absolute bottom-4 right-4 h-8 w-8 text-primary/30 sm:bottom-6 sm:right-6 sm:h-10 sm:w-10"
            viewBox="0 0 40 40"
            fill="none"
            role="img"
            aria-label="HUD corner"
          >
            <path d="M40 28v12H28" stroke="currentColor" strokeWidth="1.5" />
          </svg>

          {/* Center crosshair */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <svg
              className="h-16 w-16 text-primary/15 sm:h-20 sm:w-20"
              viewBox="0 0 80 80"
              fill="none"
              role="img"
              aria-label="Crosshair"
            >
              <circle cx="40" cy="40" r="18" stroke="currentColor" strokeWidth="0.5" />
              <path d="M40 16v8M40 56v8M16 40h8M56 40h8" stroke="currentColor" strokeWidth="0.5" />
              <path d="M40 32v2M40 46v2M32 40h2M46 40h2" stroke="currentColor" strokeWidth="1" />
            </svg>
          </div>
        </div>

        {/* === HUD DATA ELEMENTS === */}
        <div className="pointer-events-none absolute inset-0 z-4 p-5 sm:p-8" aria-hidden="true">
          {/* Top-left: REC indicator */}
          <FadeIn delay={0.8} duration={0.4} direction="left" distance={10}>
            <div className="absolute top-5 left-5 flex items-center gap-2 sm:top-8 sm:left-8">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
              </span>
              <HudText className="text-red-400/90">REC</HudText>
            </div>
          </FadeIn>

          {/* Top-right: Battery + Signal */}
          <FadeIn delay={1.0} duration={0.4} direction="right" distance={10}>
            <div className="absolute top-5 right-5 flex items-center gap-4 sm:top-8 sm:right-8">
              <div className="flex items-center gap-1.5">
                <SignalBars strength={4} />
                <HudText>RC</HudText>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="relative h-2.5 w-4.5 rounded-sm border border-primary/60">
                  <div
                    className="absolute inset-px rounded-[1px] bg-primary/70"
                    style={{ width: `${(batteryLevel / 100) * 14}px` }}
                  />
                  <div className="absolute -right-0.75 top-0.5 h-1 w-0.5 rounded-r-sm bg-primary/60" />
                </div>
                <HudText>{batteryLevel}%</HudText>
              </div>
            </div>
          </FadeIn>

          {/* Bottom-right: Coordinates + Altitude */}
          <FadeIn delay={1.2} duration={0.4} direction="right" distance={10}>
            <div className="absolute right-5 bottom-5 text-right sm:right-8 sm:bottom-8">
              <div className="flex flex-col gap-1">
                <HudText>
                  {COORDS.lat} S &nbsp; {COORDS.lon} W
                </HudText>
                <HudText>ALT 120m &middot; SPD 8.2m/s</HudText>
              </div>
            </div>
          </FadeIn>

          {/* Bottom-left: LIVE + Timestamp (above content area) */}
          <FadeIn delay={1.1} duration={0.4} direction="left" distance={10}>
            <div className="absolute bottom-52 left-5 flex items-center gap-3 sm:bottom-56 sm:left-8 md:bottom-48">
              <div className="flex items-center gap-1.5 rounded border border-primary/30 bg-primary/10 px-2 py-0.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-pulse rounded-full bg-primary" />
                </span>
                <HudText className="text-primary">LIVE</HudText>
              </div>
              <HudText>{timestamp}</HudText>
            </div>
          </FadeIn>

          {/* Top-center: Mode indicator */}
          <FadeIn delay={0.9} duration={0.4} direction="down" distance={8}>
            <div className="absolute top-5 left-1/2 -translate-x-1/2 sm:top-8">
              <HudText className="text-primary/50">P-GPS &middot; 4K/60</HudText>
            </div>
          </FadeIn>
        </div>

        {/* === MAIN CONTENT === */}
        <div className="relative z-5 w-full px-5 pb-12 sm:px-8 sm:pb-16 md:pb-20">
          <div className="mx-auto max-w-7xl">
            <FadeIn delay={0.3} duration={0.8} direction="up" distance={30}>
              <p className="mb-3 font-mono text-xs tracking-[0.25em] text-primary/70 uppercase sm:text-sm">
                Capacitação Completa em Drones Agrícolas
              </p>
            </FadeIn>

            <FadeIn delay={0.5} duration={0.8} direction="up" distance={30}>
              <h1 className="max-w-3xl font-heading text-4xl leading-[1.05] font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-7xl">
                Domine a pulverização
                <br />
                <span className="text-primary">com drones no campo.</span>
              </h1>
            </FadeIn>

            <FadeIn delay={0.7} duration={0.8} direction="up" distance={20}>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:mt-5 sm:text-base lg:text-lg">
                Curso completo de piloto agrícola: operação, mapeamento 3D e manutenção. Do zero à
                atuação segura no agro.
              </p>
            </FadeIn>

            {/* Quick value props */}
            <FadeIn delay={0.85} duration={0.6} direction="up" distance={15}>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 sm:mt-6">
                {['Pulverização', 'Mapeamento 3D', 'Manutenção'].map((item) => (
                  <span
                    key={item}
                    className="inline-flex items-center gap-2 font-mono text-[10px] tracking-widest text-foreground/60 uppercase sm:text-xs"
                  >
                    <span className="h-1 w-1 rounded-full bg-primary" />
                    {item}
                  </span>
                ))}
              </div>
            </FadeIn>

            <FadeIn delay={1.0} duration={0.6} direction="up" distance={15}>
              <div className="mt-7 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:items-center sm:gap-4">
                <button
                  type="button"
                  className="pointer-events-auto group inline-flex items-center gap-2 border border-primary/40 bg-primary/10 px-6 py-3 font-mono text-xs tracking-widest text-primary uppercase backdrop-blur-sm transition-all hover:border-primary/70 hover:bg-primary/20 sm:px-8 sm:py-3.5 sm:text-sm"
                  onClick={() => window.open(branding.contact.whatsapp, '_blank')}
                >
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
                  </span>
                  Garantir Minha Vaga
                  <svg
                    className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1"
                    viewBox="0 0 16 16"
                    fill="none"
                    role="img"
                    aria-label="Seta"
                  >
                    <path
                      d="M3 8h10M9 4l4 4-4 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  className="pointer-events-auto inline-flex items-center gap-2 px-4 py-3 font-mono text-xs tracking-widest text-muted-foreground uppercase transition-colors hover:text-foreground sm:text-sm"
                  onClick={() =>
                    document.getElementById('vsl')?.scrollIntoView({ behavior: 'smooth' })
                  }
                >
                  <PlayCircle className="h-3.5 w-3.5" />
                  Ver o Curso
                </button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}
